import React, { useState, useEffect, useMemo } from 'react';
import { getErrorMessage, getValidationErrors } from '../../../services/apiService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { KIND_TEXT, KINDS, comboKey, comboLabel, combosOfUser, groupCombos, natureOptions, parseComboKey, subcategoryOptions } from '../routing';

const ROLES = [
  { value: 'ADMIN', label: 'Administrator', description: 'Full system access' },
  { value: 'COORDINATOR', label: 'Coordinator', description: 'Manage vendors and approvals' },
  { value: 'USER', label: 'User', description: 'View only access' },
];

const canApprove = (role) => role === 'ADMIN' || role === 'COORDINATOR';
const pickClass = 'w-full min-w-0 h-8 px-2 border border-slate-300 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/50';

/**
 * The combinations of one kind this user approves: the existing ones to tick, and a row to
 * start a new one for them.
 */
const ApprovesKind = ({ kind, rules, selected, onChange }) => {
  const [nature, setNature] = useState('');
  const [sub, setSub] = useState('');
  const combos = useMemo(() => groupCombos(rules, kind), [rules, kind]);
  const known = combos.map((c) => c.key);
  const options = [...known, ...selected.filter((k) => !known.includes(k))];
  const toggle = (key) => onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  const addNew = () => {
    const key = comboKey(nature, sub);
    if (!selected.includes(key)) onChange([...selected, key]);
    setNature('');
    setSub('');
  };
  const heading = kind === 'INVITE' ? 'Vendor invites' : 'Quality (RM / PM suppliers)';
  return (
    <div data-testid={`user-approves-${kind.toLowerCase()}`}>
      <p className="text-xs font-semibold text-slate-700 mb-1.5">{heading}</p>
      {options.length > 0 && (
        <div className="space-y-1 mb-2">
          {options.map((key) => {
            const c = parseComboKey(key);
            const existing = combos.find((x) => x.key === key);
            const count = existing ? existing.people.length : 0;
            return (
              <label key={key} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={selected.includes(key)} onChange={() => toggle(key)} />
                <span className="flex-1 text-slate-800">{comboLabel(c.nature_of_business, c.supplier_subcategory)}</span>
                <span className="text-xs text-slate-500">{existing ? `${count} ${count === 1 ? 'person' : 'people'}` : 'new'}</span>
              </label>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] items-center gap-1.5">
        <select className={pickClass} value={nature} onChange={(e) => setNature(e.target.value)} aria-label={`${KIND_TEXT[kind].title}: nature of business for this user`}>
          {natureOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={pickClass} value={sub} onChange={(e) => setSub(e.target.value)} aria-label={`${KIND_TEXT[kind].title}: sub-category for this user`}>
          {subcategoryOptions(kind).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          type="button"
          onClick={addNew}
          disabled={selected.includes(comboKey(nature, sub))}
          className="h-8 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          aria-label={`Add this ${kind === 'INVITE' ? 'invite' : 'quality'} combination`}
        >
          <Icon name="Plus" size={12} />Add
        </button>
      </div>
    </div>
  );
};

const UserFormModal = ({ isOpen, onClose, onSubmit, user, mode, routingRules = [] }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    role: 'USER',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // The combinations this user approves, per kind (combination keys, see ../routing.js).
  const [approves, setApproves] = useState({ INVITE: [], QUALITY: [] });
  const hadRules = mode === 'edit' && user ? combosOfUser(routingRules, user.id) : { INVITE: [], QUALITY: [] };
  const hadCount = hadRules.INVITE.length + hadRules.QUALITY.length;

  // Initialize form when modal opens or user changes
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        full_name: user.full_name || '',
        username: user.username || '',
        email: user.email || '',
        password: '', // Never pre-fill password
        role: user.role || 'USER',
        is_active: user.is_active ?? true,
      });
    } else {
      setFormData({
        full_name: '',
        username: '',
        email: '',
        password: '',
        role: 'USER',
        is_active: true,
      });
    }
    setErrors({});
    setApproves(mode === 'edit' && user ? combosOfUser(routingRules, user.id) : { INVITE: [], QUALITY: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mode, isOpen]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters';
    }

    if (mode === 'create' && !formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username && formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username && formData.username.trim().length > 30) {
      newErrors.username = 'Username must be at most 30 characters';
    } else if (formData.username && !/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (mode === 'create' && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare data for submission
      const submitData = { ...formData };
      
      if (mode === 'edit') {
        // For edit mode, remove password and username (backend doesn't accept password in update, username can't be changed)
        delete submitData.password;
        delete submitData.username;
      } else {
        // For create mode, password is required and should be included
        if (!submitData.password) {
          setErrors({ password: 'Password is required' });
          setLoading(false);
          return;
        }
      }

      // Only a Coordinator or Administrator can approve: another role approves nothing.
      await onSubmit(submitData, canApprove(submitData.role ?? formData.role) ? approves : { INVITE: [], QUALITY: [] });
    } catch (err) {
      console.error('Form submit error:', err);
      
      // Handle validation errors from server
      const validationErrors = getValidationErrors(err);
      if (validationErrors) {
        setErrors(validationErrors);
      } else {
        setErrors({ general: getErrorMessage(err) });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">
              {mode === 'create' ? 'Add New User' : 'Edit User'}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* General Error */}
              {errors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errors.general}
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    errors.full_name ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="Enter full name"
                />
                {errors.full_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>
                )}
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Username {mode === 'create' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    errors.username ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="Enter username"
                  disabled={mode === 'edit'}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-500">{errors.username}</p>
                )}
                {mode === 'edit' && !errors.username && (
                  <p className="mt-1 text-xs text-slate-500">
                    Username cannot be changed after creation
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    errors.email ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="user@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password - Only show in create mode */}
              {mode === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                      errors.password ? 'border-red-500' : 'border-slate-300'
                    }`}
                    placeholder="Enter password"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                  )}
                </div>
              )}

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {ROLES.map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.role === role.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={formData.role === role.value}
                        onChange={handleChange}
                        className="mt-0.5 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-slate-900">
                          {role.label}
                        </div>
                        <div className="text-xs text-slate-500">
                          {role.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-500">{errors.role}</p>
                )}
              </div>

              {/* Approves: invite / quality combinations, for the roles that can open Approvals */}
              <div className="rounded-lg border border-slate-200" data-testid="user-approves-section">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 rounded-t-lg">
                  <Icon name="GitBranch" size={14} className="text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Approves</span>
                  <span className="text-xs text-slate-500">(optional)</span>
                </div>
                {canApprove(formData.role) ? (
                  <div className="p-3 space-y-4">
                    <p className="text-xs text-slate-500">
                      Tick the combinations this person approves. Everyone in a combination is e-mailed and any one of them can approve.
                    </p>
                    {KINDS.map((kind) => (
                      <ApprovesKind
                        key={kind}
                        kind={kind}
                        rules={routingRules}
                        selected={approves[kind]}
                        onChange={(keys) => setApproves((cur) => ({ ...cur, [kind]: keys }))}
                      />
                    ))}
                  </div>
                ) : (
                  <p className={`p-3 text-xs rounded-b-lg ${hadCount ? 'text-amber-800 bg-amber-50' : 'text-slate-500'}`}>
                    {hadCount
                      ? `Only Coordinators and Administrators can approve. Saving with the User role removes ${user?.full_name || 'this user'} from ${hadCount} approval combination${hadCount === 1 ? '' : 's'}.`
                      : 'Only Coordinators and Administrators can approve invites or quality. Pick one of those roles to make this person an approver.'}
                  </p>
                )}
              </div>

              {/* Status (Edit mode only) */}
              {mode === 'edit' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-slate-700">
                    User is active
                  </label>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                iconName={loading ? 'Loader2' : mode === 'create' ? 'UserPlus' : 'Save'}
                iconPosition="left"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;







